import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import { Download, ChevronDown, ChevronUp, Search, RefreshCw, Star, Mail } from 'lucide-react';

const PAYMENT_METHODS = ['', 'card', 'bank', 'cash', 'check', 'other'];

function StarRating({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={12} className={n <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
      ))}
    </span>
  );
}

function DonationRow({ d }) {
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // 'ok' | 'error' | null

  const handleSendReceipt = async (e) => {
    e.stopPropagation();
    setSending(true);
    setSendStatus(null);
    try {
      await api.sendReceipt(d.id);
      setSendStatus('ok');
    } catch (err) {
      setSendStatus(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <tr
        onClick={() => setExpanded(e => !e)}
        className="cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors border-b border-gray-100 dark:border-slate-800"
      >
        <td className="px-4 py-3 text-xs font-mono text-blue-600 dark:text-blue-400 whitespace-nowrap">{d.receipt_number}</td>
        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {new Date(d.timestamp).toLocaleString()}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{d.name}</td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{d.email}</td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{d.phone || '—'}</td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-800 dark:text-gray-200">${Number(d.amount).toFixed(2)}</td>
        <td className="px-4 py-3 text-sm text-gray-500">${Number(d.fee).toFixed(2)}</td>
        <td className="px-4 py-3 text-sm font-bold text-green-600 dark:text-green-400">${Number(d.total).toFixed(2)}</td>
        <td className="px-4 py-3">
          <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full capitalize">
            {d.payment_method}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          {d.recurring ? (
            <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">Yes</span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{d.member_id || '—'}</td>
        <td className="px-4 py-3"><StarRating value={d.service_rating} /></td>
        <td className="px-4 py-3 text-xs text-gray-500 max-w-[120px] truncate">{d.feedback || '—'}</td>
        <td className="px-4 py-3 text-center">
          {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-blue-50/50 dark:bg-slate-800/50 border-b border-blue-100 dark:border-slate-700">
          <td colSpan={14} className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Receipt #</p>
                <p className="font-mono text-blue-600 dark:text-blue-400">{d.receipt_number}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Full Timestamp</p>
                <p>{new Date(d.timestamp).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Frequency</p>
                <p>{d.frequency || (d.recurring ? 'Recurring' : 'One-Time')}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Member ID</p>
                <p>{d.member_id || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Nonprofit</p>
                <p>{d.nonprofit_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">EIN</p>
                <p>{d.nonprofit_ein || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Star Rating</p>
                <StarRating value={d.service_rating} />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Linked Account</p>
                <p>{d.donor_id ? <span className="text-green-600">Linked</span> : <span className="text-gray-400">Guest</span>}</p>
              </div>
              {d.feedback && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Feedback</p>
                  <p className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-gray-200 dark:border-slate-700">{d.feedback}</p>
                </div>
              )}
              <div className="col-span-2 md:col-span-4 flex items-center gap-3 pt-2">
                <button
                  onClick={handleSendReceipt}
                  disabled={sending || !d.email}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Mail size={14} />
                  {sending ? 'Sending…' : 'Send Receipt'}
                </button>
                {sendStatus === 'ok' && (
                  <span className="text-sm text-green-600 dark:text-green-400">Receipt sent to {d.email}</span>
                )}
                {sendStatus && sendStatus !== 'ok' && (
                  <span className="text-sm text-red-500">{sendStatus}</span>
                )}
                {!d.email && (
                  <span className="text-xs text-gray-400">No email on file</span>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminDonationsPage() {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [method, setMethod] = useState('');
  const [recurring, setRecurring] = useState('');

  const activeFilters = { from, to, method, recurring };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { donations } = await api.getDonations(activeFilters);
      setDonations(donations);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, method, recurring]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    window.location.href = api.exportUrl(activeFilters);
  };

  const total = donations.reduce((s, d) => s + Number(d.total), 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Donations</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {loading ? 'Loading…' : `${donations.length} record${donations.length !== 1 ? 's' : ''} · $${total.toFixed(2)} total`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity shadow"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Payment Method</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All methods</option>
              {PAYMENT_METHODS.filter(Boolean).map(m => (
                <option key={m} value={m} className="capitalize">{m.charAt(0).toUpperCase() + m.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type</label>
            <select
              value={recurring}
              onChange={e => setRecurring(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All types</option>
              <option value="0">One-time</option>
              <option value="1">Recurring</option>
            </select>
          </div>
          {(from || to || method || recurring) && (
            <button
              onClick={() => { setFrom(''); setTo(''); setMethod(''); setRecurring(''); }}
              className="px-3 py-2 text-sm text-red-500 hover:text-red-700 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
        {error && (
          <div className="p-4 text-sm text-red-600 bg-red-50 dark:bg-red-900/20">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/60 border-b border-gray-100 dark:border-slate-700">
                {['Receipt #', 'Date/Time', 'Name', 'Email', 'Phone', 'Amount', 'Fee', 'Total', 'Method', 'Recurring', 'Member ID', 'Rating', 'Feedback', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} className="px-4 py-12 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading donations…</span>
                  </div>
                </td></tr>
              ) : donations.length === 0 ? (
                <tr><td colSpan={14} className="px-4 py-12 text-center text-gray-400">
                  <Search size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No donations found</p>
                </td></tr>
              ) : (
                donations.map(d => <DonationRow key={d.id} d={d} />)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
